// ORKSTRA Theme — pure helpers for resolving and applying themes.
// All DOM-touching helpers are guarded so they no-op safely during SSR / tests
// that don't mount jsdom.

import { tokens } from '@/styles/design-tokens'
import type { ColorMode, ThemePreference } from './types'

const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)'

export function resolveMode(
  preference: ThemePreference,
  systemMode: ColorMode,
): ColorMode {
  return preference === 'auto' ? systemMode : preference
}

export function getSystemMode(): ColorMode {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return 'light'
  }
  return window.matchMedia(SYSTEM_DARK_QUERY).matches ? 'dark' : 'light'
}

export function subscribeToSystemMode(
  listener: (mode: ColorMode) => void,
): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return () => {}
  }
  const mq = window.matchMedia(SYSTEM_DARK_QUERY)
  const handler = (event: MediaQueryListEvent) => {
    listener(event.matches ? 'dark' : 'light')
  }
  if (mq.addEventListener) {
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }
  // Safari < 14 fallback
  mq.addListener(handler)
  return () => mq.removeListener(handler)
}

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'auto'
}

// Build the CSS variable map for a given mode. Exposed for tests + the
// no-flash inline script generator.
export function buildCssVariables(mode: ColorMode): Record<string, string> {
  const palette = tokens.colors[mode]
  const shadows = tokens.shadows[mode]
  return {
    '--bg-primary': palette.bg.primary,
    '--bg-secondary': palette.bg.secondary,
    '--bg-tertiary': palette.bg.tertiary,
    '--bg-sidebar': palette.bg.sidebar,
    '--bg-topbar': palette.bg.topbar,
    '--bg-card': palette.bg.card,

    '--text-primary': palette.text.primary,
    '--text-secondary': palette.text.secondary,
    '--text-tertiary': palette.text.tertiary,
    '--text-inverse': palette.text.inverse,
    '--text-sidebar': palette.text.sidebar,

    '--border-default': palette.border.DEFAULT,
    '--border-strong': palette.border.strong,
    '--border-subtle': palette.border.subtle,

    '--color-primary': palette.brand.primary,
    '--color-primary-hover': palette.brand.primaryHover,
    '--color-secondary': palette.brand.secondary,
    '--color-accent': palette.brand.accent,

    '--color-success': palette.semantic.success,
    '--color-warning': palette.semantic.warning,
    '--color-danger': palette.semantic.danger,
    '--color-info': palette.semantic.info,

    '--shadow-sm': shadows.sm,
    '--shadow-md': shadows.md,
    '--shadow-lg': shadows.lg,
    '--shadow-glow': shadows.glow,
  }
}

export function applyThemeToDocument(mode: ColorMode): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const vars = buildCssVariables(mode)
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value)
  }
  root.setAttribute('data-theme', mode)
  root.style.colorScheme = mode

  // Keep the browser chrome (URL bar, PWA splash) in sync with the active theme.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  const themeColor = mode === 'dark'
    ? tokens.colors.dark.bg.primary
    : tokens.colors.light.bg.primary
  if (meta) {
    meta.setAttribute('content', themeColor)
  }
}
